import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Stack,
  Chip,
  FormHelperText,
  useTheme,
} from '@mui/material';
import {
  Close,
  Person,
  Phone,
  AutoAwesome,
  Assignment,
  Add,
} from '@mui/icons-material';
import { Agent } from '../types/agent';
import { LeadQuality, Status } from '../types';
import { agentApi } from '../utils/agentApi';
import { statusApi } from '../utils/statusApi';
import { createLead, CreateLeadRequest, BackendChatHistoryRS } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface CreateLeadModalProps {
  open: boolean;
  onClose: () => void;
  onLeadCreated: (lead: any) => void;
  conversationData?: BackendChatHistoryRS | null;
  isMobile?: boolean;
}


const CreateLeadModal: React.FC<CreateLeadModalProps> = ({
  open,
  onClose,
  onLeadCreated,
  conversationData,
  isMobile
}) => {
  const { user } = useAuth();
  const theme = useTheme();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CreateLeadRequest>({
    name: '',
    agentId: undefined,
    phoneNumber: '',
    email: '',
    agentPhone: '',
    interested: '',
    integrated: false,
    resumeAI: '',
    status: 'novo',
    statusId: undefined,
    observacoes: []
  });

  const [agents, setAgents] = useState<Agent[]>([]);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiGenerating, setAiGenerating] = useState(false);

  // Load agents and statuses when modal opens
  useEffect(() => {
    if (open) {
      loadAgents();
      loadStatuses();
    }
  }, [open]);

  // Auto-select agent when agents are loaded and conversation data is available
  useEffect(() => {
    if (conversationData?.agentId && agents.length > 0) {
      const conversationAgent = agents.find(agent => agent.id === conversationData.agentId);
      if (conversationAgent) {
        setFormData(prev => ({
          ...prev,
          agentId: conversationAgent.id
        }));
      }
    }
  }, [agents, conversationData]);

  // Pre-fill form with conversation data
  useEffect(() => {
    if (open && conversationData) {
      // Generate AI resume from conversation messages
      const generateConversationResume = () => {
        const messages = conversationData.messages || [];
        const messageCount = messages.length;
        const lastMessage = messages[messages.length - 1];
        const firstMessage = messages[0];

        let resume = t('dashboard.createLead.titleFromConversation') + ':\n';
        resume += `- ${t('dashboard.createLead.name')}: ${conversationData.name || t('dashboard.createLead.notInformed')}\n`;
        resume += `- ${t('dashboard.createLead.phone')}: ${conversationData.userId}\n`;
        resume += `- ${t('dashboard.createLead.messages')}: ${messageCount}\n`;
        resume += `- ${t('dashboard.leadDetails.creationDate')}: ${new Date().toLocaleString('pt-BR')}\n\n`;

        if (firstMessage) {
          const roleLabel = firstMessage.role === 'user' ? t('dashboard.createLead.client') : t('dashboard.leadDetails.aiAssistant');
          resume += `${t('dashboard.createLead.firstMessage')} (${roleLabel}):\n`;
          resume += `"${firstMessage.content.substring(0, 200)}${firstMessage.content.length > 200 ? '...' : ''}"\n\n`;
        }

        if (lastMessage && lastMessage !== firstMessage) {
          const roleLabel = lastMessage.role === 'user' ? t('dashboard.createLead.client') : t('dashboard.leadDetails.aiAssistant');
          resume += `${t('dashboard.createLead.lastMessage')} (${roleLabel}):\n`;
          resume += `"${lastMessage.content.substring(0, 200)}${lastMessage.content.length > 200 ? '...' : ''}"\n\n`;
        }

        resume += t('dashboard.createLead.autoFilled');
        return resume;
      };

      setFormData(prev => ({
        ...prev,
        name: conversationData.name || '',
        phoneNumber: conversationData.userId || '',
        email: 'nao-informado@conversa.com', // Default email for conversation leads
        agentId: conversationData.agentId || undefined,
        resumeAI: generateConversationResume(),
        interested: t('dashboard.createLead.interestIdentified'),
        createdBy: user?.name || user?.email || t('dashboard.createLead.system') // Set current user as creator
      }));
    }
  }, [open, conversationData]);

  const loadAgents = async () => {
    try {
      setLoading(true);
      const agentsData = await agentApi.getAgentsList({
        pageIndex: 0,
        pageSize: 100,
        agentName: '',
        agentStatus: 'Enabled'
      });
      setAgents(agentsData);
    } catch (err) {
      console.error('Failed to load agents:', err);
      setError(t('dashboard.createLead.loadAgentsError'));
    } finally {
      setLoading(false);
    }
  };

  const loadStatuses = async () => {
    try {
      const statusesData = await statusApi.getStatuses();
      const sortedStatuses = statusesData.sort((a, b) => a.order - b.order);
      setStatuses(sortedStatuses);

      // Set default status to first available status
      if (sortedStatuses.length > 0) {
        const defaultStatus = sortedStatuses[0];
        setFormData(prev => ({
          ...prev,
          status: defaultStatus.name,
          statusId: defaultStatus.id
        }));
      }
    } catch (err) {
      console.error('Failed to load statuses:', err);
      // Fallback to default statuses
      const fallbackStatuses = [
        { id: 'novo', name: 'Novo', color: '#00f5ff', order: 1, isDefault: true },
        { id: 'contato_inicial', name: 'Contato Inicial', color: '#0288d1', order: 2, isDefault: true },
        { id: 'qualificado', name: 'Qualificado', color: '#388e3c', order: 3, isDefault: true },
        { id: 'proposta', name: 'Proposta', color: '#fbc02d', order: 4, isDefault: true },
        { id: 'negociacao', name: 'Negociação', color: '#f57c00', order: 5, isDefault: true },
        { id: 'fechado', name: 'Fechado', color: '#388e3c', order: 6, isDefault: true },
        { id: 'perdido', name: 'Perdido', color: '#d32f2f', order: 7, isDefault: true },
      ];
      setStatuses(fallbackStatuses);

      // Set default status to first fallback status
      setFormData(prev => ({
        ...prev,
        status: fallbackStatuses[0].name,
        statusId: fallbackStatuses[0].id
      }));
    }
  };

  const handleInputChange = (field: keyof CreateLeadRequest, value: any) => {
    if (field === 'status') {
      // When status changes, update both status and statusId
      const selectedStatus = statuses.find(s => s.id === value);
      setFormData(prev => ({
        ...prev,
        status: selectedStatus?.name || value,
        statusId: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    setError(null);
  };

  const generateAIResume = async () => {
    if (!formData.name || !formData.interested) {
      setError(t('dashboard.createLead.nameAndInterestRequired'));
      return;
    }

    try {
      setAiGenerating(true);
      setError(null);

      // Generate a basic AI resume based on the provided information
      const aiResume = `${t('dashboard.createLead.title')}:
- ${t('dashboard.createLead.name')}: ${formData.name}
- ${t('dashboard.createLead.interest')}: ${formData.interested || t('dashboard.createLead.notInformed')}
- ${t('dashboard.createLead.phone')}: ${formData.phoneNumber}
- ${t('dashboard.createLead.emailLabel')}: ${formData.email}
- ${t('dashboard.leadDetails.creationDate')}: ${new Date().toLocaleString('pt-BR')}

${t('dashboard.createLead.leadCreatedManually')}`;

      setFormData(prev => ({
        ...prev,
        resumeAI: aiResume
      }));
    } catch (err) {
      setError(t('dashboard.createLead.generateError'));
    } finally {
      setAiGenerating(false);
    }
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError(t('dashboard.createLead.nameRequired'));
      return false;
    }
    if (!formData.phoneNumber.trim()) {
      setError(t('dashboard.createLead.phoneRequired'));
      return false;
    }
    if (!formData.resumeAI.trim()) {
      setError(t('dashboard.createLead.summaryRequired'));
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      setError(null);

      const leadData = {
        ...formData,
        leadQuality: 'Neutro' as LeadQuality,
        observacoes: formData.observacoes || [],
        assignedTo: user?.id,
        assignedToName: user?.name
      };

      const createdLead = await createLead(leadData);

      toast.success(t('dashboard.createLead.createSuccess'));
      onLeadCreated(createdLead);
      handleClose();
    } catch (err: any) {
      const errorMessage = err?.message || t('dashboard.createLead.createError');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      agentId: undefined,
      phoneNumber: '',
      email: '',
      agentPhone: '',
      interested: '',
      integrated: false,
      resumeAI: '',
      status: 'novo',
      observacoes: []
    });
    setError(null);
    onClose();
  };

  const selectedAgent = agents.find(agent => agent.id === formData.agentId);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: isMobile ? 0 : 2,
          margin: isMobile ? 0 : '24px',
          maxWidth: isMobile ? '100vw' : '800px',
          maxHeight: isMobile ? '100vh' : '90vh',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: theme.palette.mode === 'dark'
            ? '#1a1a1a'
            : 'linear-gradient(135deg, #1a1a1a 0%, #34495e 100%)',
          p: 2,
          minHeight: '60px'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {conversationData ? t('dashboard.createLead.titleFromConversation') : t('dashboard.createLead.title')}
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{
            color: theme.palette.mode === 'dark' ? '#000' : undefined,
            backgroundColor: 'rgba(255,255,255,0.2)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={3} mt={2}>
          {/* Conversation Info */}
          {conversationData && (
            <Box sx={{
              p: 2,
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(14,165,233,0.15)'
                : 'rgba(14,165,233,0.1)',
              borderRadius: 1,
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(14,165,233,0.4)'
                : '1px solid rgba(14,165,233,0.3)'
            }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'info.main' }}>
                {t('dashboard.createLead.conversationInfo')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('dashboard.createLead.name')}:</strong> {conversationData.name || t('dashboard.createLead.notInformed')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('dashboard.createLead.phone')}:</strong> {conversationData.userId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('dashboard.createLead.messages')}:</strong> {conversationData.messages?.length || 0} {t('dashboard.createLead.messagesInConversation')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('dashboard.agent')}:</strong> {agents.find(a => a.id === conversationData.agentId)?.name || t('dashboard.createLead.loading')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('dashboard.createLead.createdBy')}:</strong> {user?.name || user?.email || t('dashboard.createLead.system')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                {t('dashboard.createLead.autoFilled')}
              </Typography>
            </Box>
          )}

          <Box>
            <Typography variant="h6" sx={{ mb: 2, mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person sx={{ fontSize: 20 }} />
              {t('dashboard.createLead.basicInfo')}
            </Typography>
            <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
              <TextField
                label={t('dashboard.createLead.leadName')}
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                fullWidth
                required
                placeholder={t('dashboard.createLead.leadNamePlaceholder')}
              />
              <TextField
                label={t('dashboard.createLead.phone')}
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                fullWidth
                required
                placeholder={t('dashboard.createLead.phonePlaceholder')}
                sx={{ minWidth: { sm: '200px' } }}
              />
            </Stack>
            <TextField
              label={t('dashboard.createLead.email')}
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              fullWidth
              placeholder={t('dashboard.createLead.emailPlaceholder')}
              sx={{ mt: 2 }}
            />
          </Box>

          {/* Agente e Configurações */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assignment sx={{ fontSize: 20 }} />
              {t('dashboard.createLead.agentAndSettings')}
            </Typography>
            <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
              <FormControl fullWidth>
                <InputLabel>{t('dashboard.createLead.agentOptional')}</InputLabel>
                <Select
                  value={formData.agentId || ''}
                  label={t('dashboard.createLead.agentOptional')}
                  onChange={(e) => handleInputChange('agentId', e.target.value || undefined)}
                  disabled={loading || !!(conversationData && conversationData.agentId)}
                >
                  <MenuItem value="">
                    <Typography variant="body2" color="text.secondary">
                      {t('dashboard.createLead.noAgentSelected')}
                    </Typography>
                  </MenuItem>
                  {agents.map((agent) => (
                    <MenuItem key={agent.id} value={agent.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {agent.name}
                        </Typography>
                        <Chip
                          label={agent.status}
                          size="small"
                          color={agent.status === 'Enabled' ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {conversationData && conversationData.agentId && (
                  <FormHelperText>
                    {t('dashboard.createLead.agentAutoSelected')}
                  </FormHelperText>
                )}
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>{t('dashboard.status')}</InputLabel>
                <Select
                  value={formData.statusId || ''}
                  label={t('dashboard.status')}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  {statuses.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: status.color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {status.name}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>

          {/* Resumo */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoAwesome sx={{ fontSize: 20 }} />
                {t('dashboard.createLead.summary')}
              </Typography>
            </Box>
            <TextField
              label={t('dashboard.createLead.summary')}
              value={formData.resumeAI}
              onChange={(e) => handleInputChange('resumeAI', e.target.value)}
              fullWidth
              multiline
              rows={4}
              required
              placeholder={t('dashboard.createLead.summaryPlaceholder')}
            />
          </Box>

          {/* Informações do Agente Selecionado */}
          {selectedAgent ? (
            <Box sx={{
              p: 2,
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.05)'
                : 'rgba(0,0,0,0.05)',
              borderRadius: 1,
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255,255,255,0.1)'
                : '1px solid rgba(0,0,0,0.1)'
            }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {t('dashboard.createLead.selectedAgent')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('dashboard.createLead.name')}:</strong> {selectedAgent.name}
              </Typography>
            </Box>
          ) : (
            <Box sx={{
              p: 2,
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255,193,7,0.15)'
                : 'rgba(255,193,7,0.1)',
              borderRadius: 1,
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255,193,7,0.4)'
                : '1px solid rgba(255,193,7,0.3)'
            }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'warning.main' }}>
                {t('dashboard.createLead.noAgentWarning')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.createLead.noAgentMessage')}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: 'background.paper', borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
        <Button onClick={handleClose} color="inherit" disabled={saving}>
          {t('dashboard.createLead.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !formData.name || !formData.phoneNumber || !formData.resumeAI}
          startIcon={saving ? <CircularProgress size={16} /> : <Add />}
          sx={{
            color: theme.palette.mode === 'dark' ? '#000' : undefined,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#0284c7' : '#0284c7',
            },
            '&:disabled': {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
            },
          }}
        >
          {saving ? t('dashboard.createLead.creating') : t('dashboard.createLead.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateLeadModal;